// Calendar visualization helpers — normalize the four existing CRM
// sources (portal events, lead follow-ups, CRM tasks, AEP prompt) into a
// single timeline-item shape the Month/Week/Day/List views can render.
// Pure logic only — no JSX. Reuses existing backend data; no new APIs.

// ── Event-type visual identity ─────────────────────────────────────
// Subtle, professional palette keyed off the event's own type/category.
// Mirrors the taxonomy the Event wizard already uses.
const PALETTE = {
  sky:     { dot: 'bg-sky-500',     soft: 'bg-sky-50 text-sky-700 border-sky-200',         bar: 'bg-sky-500' },
  emerald: { dot: 'bg-emerald-500', soft: 'bg-emerald-50 text-emerald-700 border-emerald-200', bar: 'bg-emerald-500' },
  indigo:  { dot: 'bg-indigo-500',  soft: 'bg-indigo-50 text-indigo-700 border-indigo-200',  bar: 'bg-indigo-500' },
  rose:    { dot: 'bg-rose-500',    soft: 'bg-rose-50 text-rose-700 border-rose-200',       bar: 'bg-rose-500' },
  amber:   { dot: 'bg-amber-500',   soft: 'bg-amber-50 text-amber-800 border-amber-200',     bar: 'bg-amber-500' },
  violet:  { dot: 'bg-violet-500',  soft: 'bg-violet-50 text-violet-700 border-violet-200',  bar: 'bg-violet-500' },
  slate:   { dot: 'bg-slate-500',   soft: 'bg-slate-100 text-slate-700 border-slate-300',    bar: 'bg-slate-500' },
};

// event_type / category → palette key + human label.
const TYPE_MAP = {
  training:          ['sky', 'Training'],
  certification:     ['emerald', 'Certification'],
  webinar:           ['indigo', 'Webinar'],
  carrier_event:     ['indigo', 'Carrier Event'],
  carrier_webinar:   ['indigo', 'Carrier Webinar'],
  conference:        ['violet', 'Conference'],
  community_event:   ['rose', 'Community'],
  grassroots_event:  ['rose', 'Community'],
  food_pantry:       ['rose', 'Community'],
  recruiting_event:  ['amber', 'Recruiting'],
  recruiting:        ['amber', 'Recruiting'],
  company_event:     ['slate', 'KR Meeting'],
  agency_event:      ['slate', 'Agency Event'],
  office_event:      ['slate', 'Office Event'],
  meeting:           ['slate', 'Meeting'],
  internal_kr_meeting: ['slate', 'KR Meeting'],
  informational:     ['slate', 'Info'],
};

const RECOGNITION = ['amber', 'Recognition'];

export function themeForEvent(ev) {
  const key = String(ev?.event_type || ev?.category || '').toLowerCase();
  if (String(ev?.business_category || '').toLowerCase() === 'recognition') {
    const [p, label] = RECOGNITION;
    return { ...PALETTE[p], label, paletteKey: p };
  }
  const [p, label] = TYPE_MAP[key] || ['sky', 'Event'];
  return { ...PALETTE[p], label, paletteKey: p };
}

export function paletteByKey(key) {
  return PALETTE[key] || PALETTE.sky;
}

// ── Date utilities ─────────────────────────────────────────────────
export const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
export const endOfDay = (d) => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x; };
export const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
export const startOfWeek = (d) => { const x = startOfDay(d); x.setDate(x.getDate() - x.getDay()); return x; };
export const startOfMonth = (d) => { const x = startOfDay(d); x.setDate(1); return x; };
export const endOfMonth = (d) => { const x = new Date(d); x.setMonth(x.getMonth() + 1, 0); x.setHours(23, 59, 59, 999); return x; };
export const sameDay = (a, b) => a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
export const isToday = (d) => sameDay(d, new Date());

// Date-only strings ('YYYY-MM-DD') must be read in LOCAL time, otherwise
// new Date() treats them as UTC midnight and they drift to the previous
// day for users in negative-offset timezones (e.g. America/New_York).
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
export const isDateOnly = (v) => typeof v === 'string' && DATE_ONLY.test(v);
const toDate = (v) => {
  if (!v) return null;
  if (isDateOnly(v)) {
    const [y, m, d] = v.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

// ── Normalize the four sources into a single item shape ────────────
// { key, kind, id, title, subtitle, start:Date, end:Date, allDay, theme, raw }
export function buildCalendarItems({ events = [], followUps = [], crmTasks = [], aep = null }) {
  const items = [];

  for (const ev of events) {
    const start = toDate(ev.start_datetime);
    if (!start) continue;
    let end = toDate(ev.end_datetime);
    if (!end || end <= start) end = new Date(start.getTime() + 60 * 60 * 1000);
    items.push({
      key: `ev-${ev.event_id}`,
      kind: 'event',
      id: ev.event_id,
      title: ev.title,
      subtitle: ev.category,
      start, end,
      allDay: !!ev.all_day,
      theme: themeForEvent(ev),
      raw: ev,
    });
  }

  for (const fu of followUps) {
    const start = toDate(fu.scheduled_at);
    if (!start) continue;
    items.push({
      key: `fu-${fu.follow_up_id}`,
      kind: 'follow_up',
      id: fu.follow_up_id,
      title: `Follow-up: ${fu.lead_name || 'Lead'}`,
      subtitle: fu.note || (fu.lead_phone ? `Phone: ${fu.lead_phone}` : 'Lead follow-up'),
      start, end: new Date(start.getTime() + 30 * 60 * 1000),
      allDay: false,
      theme: { ...PALETTE.violet, label: 'Follow-up', paletteKey: 'violet' },
      raw: fu,
    });
  }

  for (const task of crmTasks) {
    const start = toDate(task.due_date);
    if (!start) continue;
    const allDay = isDateOnly(task.due_date);
    items.push({
      key: `crm-${task.followup_id}`,
      kind: 'crm_task',
      id: task.followup_id,
      title: task.title || 'CRM Task',
      subtitle: task.description || 'CRM follow-up task',
      start,
      end: allDay ? endOfDay(start) : new Date(start.getTime() + 30 * 60 * 1000),
      allDay,
      theme: { ...PALETTE.amber, label: 'CRM Task', paletteKey: 'amber' },
      raw: task,
    });
  }

  if (aep && aep.pending > 0 && aep.dueDateIso) {
    const start = toDate(aep.dueDateIso);
    if (start) {
      items.push({
        key: 'aep-summary',
        kind: 'aep',
        id: 'aep',
        title: 'AEP Certifications Pending',
        subtitle: `${aep.pending} of ${aep.total} carrier certifications remain`,
        start, end: endOfDay(start),
        allDay: true,
        theme: { ...PALETTE.emerald, label: 'AEP', paletteKey: 'emerald' },
        raw: aep,
      });
    }
  }

  items.sort((a, b) => a.start - b.start);
  return items;
}

export function itemsForDay(items, day) {
  return items.filter((it) => sameDay(it.start, day));
}

// Compute the AEP prompt date: surface it ~2 weeks before the Oct 15 AEP
// season start and keep it live through the season (ends Dec 7), only
// rolling to next year once this season has fully passed.
export function computeAepDate(now = new Date()) {
  const year = now.getFullYear();
  let due = new Date(year, 9, 15); // Oct 15
  const seasonEnd = new Date(year, 11, 7, 23, 59, 59);
  if (now.getTime() > seasonEnd.getTime()) due = new Date(year + 1, 9, 15);
  return due.toISOString();
}

// ── Lane packing for time-grid overlap (Week/Day views) ────────────
// Groups overlapping timed items into clusters and assigns each a lane
// index + the cluster's total lane count so blocks can sit side-by-side.
export function packLanes(dayItems) {
  const timed = dayItems
    .filter((it) => !it.allDay)
    .map((it) => ({ ...it, _s: it.start.getTime(), _e: it.end.getTime() }))
    .sort((a, b) => a._s - b._s || b._e - a._e);

  const out = [];
  let cluster = [];
  let clusterEnd = -Infinity;

  const flush = () => {
    if (!cluster.length) return;
    const laneEnds = [];
    for (const ev of cluster) {
      let placed = false;
      for (let i = 0; i < laneEnds.length; i++) {
        if (ev._s >= laneEnds[i]) { ev._lane = i; laneEnds[i] = ev._e; placed = true; break; }
      }
      if (!placed) { ev._lane = laneEnds.length; laneEnds.push(ev._e); }
    }
    const total = laneEnds.length;
    for (const ev of cluster) { ev._lanes = total; out.push(ev); }
    cluster = [];
    clusterEnd = -Infinity;
  };

  for (const ev of timed) {
    if (ev._s >= clusterEnd && cluster.length) flush();
    cluster.push(ev);
    clusterEnd = Math.max(clusterEnd, ev._e);
  }
  flush();
  return out;
}

export const DAY_START_HOUR = 6;
export const DAY_END_HOUR = 22;
export const HOUR_H = 48;

export function minutesFromDayStart(date) {
  return (date.getHours() - DAY_START_HOUR) * 60 + date.getMinutes();
}
